/**
 * ScoringStudio - Unified scoring configuration step for the template creation wizard.
 *
 * Composes ScoringEmphasisPanel and RegionAnnotator into a single cohesive step
 * with clear explanations of what each section controls and how they interact.
 *
 * SCORING MODEL NOTE (2026-06-03, B-2026-0055):
 * The previous "Full-Body Baseline" section (GlobalBodyMatchSettings) has been
 * replaced by the unified ScoringEmphasisPanel. GlobalBodyMatchSettings is no longer
 * rendered here. The payload fields it wrote (global_body_match_enabled,
 * global_body_match_weight) are still written via CreateTemplate.tsx — ScoringEmphasisPanel
 * uses the same props/callbacks, so the save payload is unchanged.
 *
 * The Economy of Motion section has been absorbed into ScoringEmphasisPanel to avoid
 * duplicate controls; it is no longer a separate numbered card.
 *
 * SPEED MODE NOTE (P0-3 → P1-3a → P1-4, B-2026-0113 / B-2026-0122 / B-2026-0123):
 * The master "Does speed matter?" toggle (speedMode = 'off' | 'on') defaults OFF and
 * writes metadata.speed_mode. The per-phase speed intent dropdown persists the NEW
 * vocabulary (match / reward_faster / reward_slower / off). The backend normalizes
 * both old and new vocab on read (normalize_speed_intent_to_internal in
 * speed_assessment.py), so not-yet-migrated templates continue to work.
 *
 * P1-4 (B-2026-0123): adds the "How much does speed count?" segmented weight control
 * (A little / A fair amount / A lot → 0.10 / 0.15 / 0.25) and the mandatory R7
 * before/after score preview. The preview is only fetched in edit mode (templateId
 * present): in create mode there is no saved evaluation to diff against, so no
 * acknowledgment is required. The parent (CreateTemplate) resets speedAcknowledged
 * to false whenever speed settings change, driving a re-fetch here.
 */

import { useState, useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import { InformationCircleIcon, ChevronDownIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { RegionAnnotator, GoalAuthoringSection, type Annotation, type RegionAnnotatorHandle } from '@/components/canvas';
import { ScoringEmphasisPanel } from './ScoringEmphasisPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import type { Phase } from '@/types/api';
import type { FormEffectCurve } from '@/types/goalAuthoring';
import { fetchSpeedPreview } from '@/services/templateApi';

// ── Speed intent types ──────────────────────────────────────────────────────
// P1-3a (B-2026-0122): persisted values now use the NEW vocabulary.
// The backend normalizes old+new vocab on read (normalize_speed_intent_to_internal).
type SpeedIntent = 'match' | 'reward_faster' | 'reward_slower' | 'off';

interface SpeedIntentOption {
  /** Stored value written to phase_speed_intents — new vocabulary as of P1-3a. */
  storedValue: SpeedIntent;
  /** Human label shown in the dropdown UI only. */
  label: string;
  /** One-line help text shown below each option via optgroup/title. */
  help: string;
  /** Glyph shown in the phase timeline summary. */
  glyph: string;
}

/**
 * P1-3a (B-2026-0122): stored values now use the NEW vocabulary.
 * `label` / `help` / `glyph` are cosmetic and stay unchanged.
 * Backend normalizes both old + new vocab on read, so not-yet-migrated
 * templates continue to score correctly.
 */
const SPEED_INTENT_OPTIONS: SpeedIntentOption[] = [
  {
    storedValue: 'match',
    label: 'Same as reference',
    help: 'Score down if faster or slower than your demo.',
    glyph: '=',
  },
  {
    storedValue: 'reward_faster',
    label: 'Faster is better',
    help: 'Only too slow costs points; matching-or-faster = full credit.',
    glyph: '⚡',
  },
  {
    storedValue: 'reward_slower',
    label: 'Slower is better',
    help: 'Only too fast costs points; matching-or-slower = full credit.',
    glyph: '🐢',
  },
  {
    storedValue: 'off',
    label: "Don't score speed here",
    help: 'Ignore speed for this phase (form only).',
    glyph: '∅',
  },
];

const SHORT_PHASE_THRESHOLD_S = 0.5; // phases under 0.5 s get a reliability warning

// ── Speed weight control (P1-4) ─────────────────────────────────────────────
// PRESERVE: these values must stay in sync with SpeedAssessmentConfig defaults
// in speed_assessment.py. DEFAULT_SPEED_WEIGHT must equal the backend default.
const DEFAULT_SPEED_WEIGHT = 0.15;

const SPEED_WEIGHT_OPTIONS = [
  { label: 'A little', value: 0.10 },
  { label: 'A fair amount', value: 0.15 },
  { label: 'A lot', value: 0.25 },
] as const;

type SpeedWeightValue = (typeof SPEED_WEIGHT_OPTIONS)[number]['value'];

// ── Preview state (P1-4 R7 safeguard) ───────────────────────────────────────

type SpeedPreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; sampleLabel: string | null; beforeOverall: number; afterOverall: number; delta: number }
  | { status: 'no_sample' }
  | { status: 'error' };

function getSpeedOption(intent: SpeedIntent): SpeedIntentOption {
  return SPEED_INTENT_OPTIONS.find((o) => o.storedValue === intent) ?? SPEED_INTENT_OPTIONS[0];
}

function getSpeedEcho(phaseName: string, intent: SpeedIntent): string {
  switch (intent) {
    case 'match':
      return `${phaseName}: match the reference speed.`;
    case 'reward_faster':
      return `${phaseName}: reward an explosive, faster execution.`;
    case 'reward_slower':
      return `${phaseName}: reward a controlled, slower execution.`;
    case 'off':
      return `${phaseName}: speed not scored here.`;
  }
}

// ── Component props ─────────────────────────────────────────────────────────

export interface ScoringStudioProps {
  videoId: string;
  videoUrl: string;
  fps: number;
  totalFrames: number;
  videoWidth: number;
  videoHeight: number;
  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  templateHandedness: 'right' | 'left' | 'ambidextrous';
  /**
   * Detected lead side from the backend — threaded from CreateTemplate for FE#4.
   * Passed through to RegionAnnotator → AnnotationCanvasOverlay so axis/joint-angle
   * previews resolve functional landmarks (lead_*, trail_*) correctly.
   */
  detectedLeadSide?: 'left' | 'right';
  phases: Phase[];
  /** @deprecated Lead side is auto-detected from video */
  facingDirection?: 'left' | 'right';
  // Global body match settings — still written to payload as-is
  globalBodyMatchEnabled: boolean;
  onGlobalBodyMatchEnabledChange: (enabled: boolean) => void;
  globalBodyMatchWeight: number;
  onGlobalBodyMatchWeightChange: (weight: number) => void;
  rotationSensitive: boolean;
  onRotationSensitiveChange: (enabled: boolean) => void;
  // Economy of motion settings
  economyOfMotionWeight: number;
  onEconomyOfMotionWeightChange: (weight: number) => void;
  // Per-phase speed intent
  phaseSpeedIntents?: Record<string, SpeedIntent>;
  onPhaseSpeedIntentChange?: (phaseId: string, intent: SpeedIntent) => void;
  /**
   * Template camera angle — the closed B3 vocabulary ('side' | 'frontal' | 'diagonal' | 'other').
   * Legacy templates may still carry the retired 'front' spelling.
   * Threaded to RegionAnnotator → TimingGoalAnnotation so kinetic-chain timing
   * goals show a side-view advisory banner (KS1). Optional — undefined = no banner.
   */
  cameraView?: string;
  /**
   * P0-3/P1-1/P1-2 — master speed scoring switch. Default 'off' (form-only).
   * When 'on', speed factors into athlete scores (P1-1/P1-2 shipped).
   */
  speedMode?: 'off' | 'on';
  onSpeedModeChange?: (mode: 'off' | 'on') => void;
  /**
   * P1-4 (B-2026-0123) — speed weight control. One of 0.10 / 0.15 / 0.25.
   * Shown only when speedMode='on'. Defaults to 0.15 when unset.
   * Writes metadata.speed_weight.
   */
  speedWeight?: number;
  onSpeedWeightChange?: (weight: number) => void;
  /**
   * P1-4 — template ID for the before/after score preview endpoint.
   * Only passed in edit mode; undefined in create mode (no saved eval exists yet).
   * When undefined, the preview is skipped and acknowledgment is not required.
   */
  templateId?: string;
  /**
   * P1-4 — whether the coach has acknowledged the speed scoring impact.
   * Managed by the parent (CreateTemplate): resets to false whenever speedMode,
   * speedWeight, or phaseSpeedIntents changes (edit mode only). Defaults to true
   * when not provided so the gate does not activate for callers that don't wire
   * it up (e.g. PerFigureAnnotationTabs in positional mode).
   */
  speedAcknowledged?: boolean;
  onSpeedAcknowledgedChange?: (acknowledged: boolean) => void;
  /**
   * PR-4 — the side the coach DECLARED as leading the movement, if they have.
   * Threaded to RegionAnnotator, which uses it for one line saying what will
   * happen to the focus areas still waiting on it. Distinct from
   * `detectedLeadSide`: that is a detection used for canvas previews, and only
   * a declaration resolves a focus area (plan §C.3a).
   */
  declaredLeadSide?: 'left' | 'right';
  /**
   * FE-3 — a check the coach should be looking at right now, by annotation id.
   *
   * Set by the parent the moment a suggestion becomes a check, so the
   * checkpoint list OPENS it with its settings filled in. The old copy promised
   * exactly this ("Accept opens the goal config form pre-filled") while the
   * check was appended silently and the only way to its settings was the pencil
   * icon a coach had to already know about.
   *
   * One-shot: `onCheckpointOpened` fires once it has been opened, and the
   * parent clears the request. It is NOT a selection the parent owns — the
   * coach's own clicks in the list keep working exactly as they did.
   */
  openCheckpointId?: string | null;
  onCheckpointOpened?: () => void;
  /**
   * Goal Score PR-7b — the template's coach-authored Form-effect curve
   * (`metadata.form_effect_curve`). Optional: a template with no Goals
   * never shows the curve editor at all (see GoalAuthoringSection's own
   * "only once at least one Goal exists" rule), so a caller that doesn't
   * thread these two props simply never surfaces the control.
   */
  formEffectCurve?: FormEffectCurve;
  onFormEffectCurveChange?: (curve: FormEffectCurve) => void;
}

// ── Main component ──────────────────────────────────────────────────────────

function ScoringStudio({
  videoId,
  videoUrl,
  fps,
  totalFrames,
  videoWidth,
  videoHeight,
  annotations,
  onAnnotationsChange,
  templateHandedness,
  detectedLeadSide,
  phases,
  facingDirection: _facingDirection,
  globalBodyMatchEnabled,
  onGlobalBodyMatchEnabledChange,
  globalBodyMatchWeight,
  onGlobalBodyMatchWeightChange,
  rotationSensitive: _rotationSensitive,
  onRotationSensitiveChange: _onRotationSensitiveChange,
  economyOfMotionWeight,
  onEconomyOfMotionWeightChange,
  phaseSpeedIntents = {},
  onPhaseSpeedIntentChange,
  cameraView,
  speedMode = 'off',
  onSpeedModeChange,
  speedWeight,
  onSpeedWeightChange,
  templateId,
  speedAcknowledged,
  onSpeedAcknowledgedChange,
  declaredLeadSide,
  openCheckpointId,
  onCheckpointOpened,
  formEffectCurve,
  onFormEffectCurveChange,
}: ScoringStudioProps) {
  const [howItWorksExpanded, setHowItWorksExpanded] = useState(false);
  const [phaseCustomizeExpanded, setPhaseCustomizeExpanded] = useState(false);
  // Goal Score follow-ups batch 1, item C: lets the Goals Card (rendered
  // below, as a SIBLING of RegionAnnotator's Card, not a child) request a
  // pick on RegionAnnotator's own video/skeleton overlay via the imperative
  // handle RegionAnnotator now exposes — reuses that component's existing
  // landmark-picker hit-testing with zero duplication.
  const regionAnnotatorRef = useRef<RegionAnnotatorHandle>(null);
  // P1-4: preview state — fetched when speedOn + templateId + not yet acknowledged
  const [previewState, setPreviewState] = useState<SpeedPreviewState>({ status: 'idle' });

  const speedOn = speedMode === 'on';
  // PRESERVE: effectiveSpeedWeight must equal DEFAULT_SPEED_WEIGHT (0.15) when prop is absent.
  const effectiveSpeedWeight: SpeedWeightValue =
    (SPEED_WEIGHT_OPTIONS.find((o) => o.value === speedWeight)?.value) ?? DEFAULT_SPEED_WEIGHT;
  // Stable string key for phaseSpeedIntents so effect deps don't churn on every render
  const phaseSpeedIntentsKey = JSON.stringify(phaseSpeedIntents);

  // P1-4 R7 safeguard: fetch before/after score preview when speed is on, the template
  // is saved (templateId present), and the coach has not acknowledged the current settings.
  // Create-mode (no templateId): preview skipped — no saved evaluation to diff against.
  // After acknowledging (speedAcknowledged=true): clears preview and stops polling.
  useEffect(() => {
    const notAcknowledged = !(speedAcknowledged ?? true);
    if (!speedOn || !templateId || !notAcknowledged) {
      setPreviewState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    setPreviewState({ status: 'loading' });

    fetchSpeedPreview(templateId, effectiveSpeedWeight)
      .then((result) => {
        if (cancelled) return;
        if (
          result.status === 'ok' &&
          result.before_overall != null &&
          result.after_overall != null &&
          result.delta != null
        ) {
          setPreviewState({
            status: 'ok',
            sampleLabel: result.sample_label,
            beforeOverall: result.before_overall,
            afterOverall: result.after_overall,
            delta: result.delta,
          });
        } else {
          setPreviewState({ status: 'no_sample' });
        }
      })
      .catch(() => {
        if (!cancelled) setPreviewState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  // phaseSpeedIntentsKey is intentionally stable — it changes when per-phase intents
  // change, triggering a re-fetch so the preview is re-confirmed after any change.
   
  }, [speedOn, templateId, effectiveSpeedWeight, phaseSpeedIntentsKey, speedAcknowledged]);

  // Build short-phase warnings when speed is on.
  const shortPhaseWarnings: string[] = [];
  if (speedOn && fps > 0) {
    for (const phase of phases) {
      const durationS = phase.duration_frames / fps;
      if (durationS < SHORT_PHASE_THRESHOLD_S) {
        shortPhaseWarnings.push(
          `"${phase.name || phase.id}" is ${durationS.toFixed(1)}s — speed scoring on very short phases is unreliable; consider merging or choosing "Don't score speed here".`
        );
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Intro banner */}
      <div className="rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 p-5 text-white shadow-sm">
        <h2 className="text-xl font-semibold">Scoring Studio</h2>
        <p className="mt-1 text-sm text-white/80">
          Set what matters most for this movement. These settings control how athlete performance is weighted and scored.
        </p>
      </div>

      {/* ── Speed master switch (P0-3) ─────────────────────────────────────── */}
      <Card variant="outlined" className="border-border-subtle" data-testid="speed-mode-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent text-base font-bold">
              1
            </span>
            <div>
              <CardTitle className="text-base text-text-primary">Does speed matter for this movement?</CardTitle>
              <p className="text-sm text-text-muted mt-0.5">
                Turn on to reward faster or slower execution per phase.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {/* Toggle row */}
          <div className="flex items-start gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={speedOn}
              aria-label="Enable speed feedback"
              data-testid="speed-mode-toggle"
              onClick={() => onSpeedModeChange?.(speedOn ? 'off' : 'on')}
              className={cn(
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent',
                'transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                speedOn ? 'bg-accent' : 'bg-dark-elevated-2'
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow',
                  'transform transition duration-200 ease-in-out',
                  speedOn ? 'translate-x-5' : 'translate-x-0'
                )}
              />
            </button>
            <div>
              {!speedOn ? (
                <p className="text-sm text-text-secondary">
                  <span className="font-medium text-text-primary">Off</span> — scored on form &amp; positions only,
                  at any speed. Good for holds, poses, and slow technique drills.
                </p>
              ) : (
                <p className="text-sm text-text-secondary">
                  <span className="font-medium text-text-primary">On</span> — speed now factors into athlete
                  scores. "Same as reference" phases penalize moving too fast or too slow.
                </p>
              )}
            </div>
          </div>

          {/* On-state: reference speed education note */}
          {speedOn && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2">
              <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
              <p className="text-xs text-amber-200">
                <span className="font-medium">Your demo speed IS the target.</span> If you filmed the
                reference slowly, the AI will flag athletes who go faster — even if that's the goal.
                Film the reference at the speed you want athletes to match.
              </p>
            </div>
          )}

          {/* On-state: "How much does speed count?" weight control (P1-4) */}
          {speedOn && (
            <div className="space-y-2" data-testid="speed-weight-control">
              <p className="text-xs font-medium text-text-secondary">How much does speed count?</p>
              <div
                className="flex overflow-hidden rounded-lg border border-border-subtle"
                role="group"
                aria-label="Speed weight"
              >
                {SPEED_WEIGHT_OPTIONS.map((opt) => {
                  const isSelected = effectiveSpeedWeight === opt.value;
                  // Produce fixed 3-digit IDs: 0.10→"010", 0.15→"015", 0.25→"025"
                  const testId = `speed-weight-btn-${Math.round(opt.value * 100).toString().padStart(3, '0')}`;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onSpeedWeightChange?.(opt.value)}
                      aria-pressed={isSelected}
                      data-testid={testId}
                      className={cn(
                        'flex-1 py-1.5 text-xs font-medium transition-colors',
                        'border-r border-border-subtle last:border-r-0',
                        isSelected
                          ? 'bg-accent text-text-inverse'
                          : 'bg-dark-elevated-1 text-text-secondary hover:text-text-primary hover:bg-dark-elevated-2',
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-text-muted">
                "A fair amount" (default) adds meaningful feedback without dominating the form score.
              </p>
            </div>
          )}

          {/* On-state: before/after score preview + acknowledge gate (P1-4 R7) */}
          {speedOn && (
            <div data-testid="speed-preview-section">
              {!templateId ? (
                // Create mode — no saved template to diff against; no ack required.
                <div className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-3 flex items-start gap-2">
                  <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted" />
                  <p className="text-xs text-text-muted" data-testid="speed-preview-no-template">
                    Save this template first to see how speed settings affect existing athlete scores.
                  </p>
                </div>
              ) : !(speedAcknowledged ?? true) ? (
                <>
                  {previewState.status === 'loading' && (
                    <div
                      className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-3 flex items-center gap-2"
                      data-testid="speed-preview-loading"
                    >
                      <svg
                        className="h-4 w-4 animate-spin text-text-muted flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      <p className="text-xs text-text-muted">Calculating score impact...</p>
                    </div>
                  )}

                  {previewState.status === 'ok' && (
                    <div
                      className={cn(
                        'rounded-lg border p-3 space-y-2',
                        previewState.delta < 0
                          ? 'border-amber-500/40 bg-amber-500/10'
                          : 'border-success/40 bg-success/10',
                      )}
                      data-testid="speed-preview-result"
                    >
                      <p className="text-xs font-medium text-text-secondary">
                        Score impact on "{previewState.sampleLabel ?? 'sample athlete'}"
                      </p>
                      <p className="text-sm font-semibold text-text-primary">
                        Before:{' '}
                        <span className="text-text-secondary">{previewState.beforeOverall.toFixed(1)}</span>
                        {' '}&rarr;{' '}
                        After:{' '}
                        <span className={previewState.delta < 0 ? 'text-amber-300' : 'text-success'}>
                          {previewState.afterOverall.toFixed(1)}
                        </span>
                        {' '}
                        <span className={cn(
                          'text-xs font-medium',
                          previewState.delta < 0 ? 'text-amber-400' : 'text-success',
                        )}>
                          ({previewState.delta >= 0 ? '+' : ''}{previewState.delta.toFixed(1)})
                        </span>
                      </p>
                      <p className="text-xs text-text-muted">
                        This is how the score would change if you save with these speed settings.
                        Enabling speed scoring changes how all athletes are graded against this template.
                      </p>
                      <button
                        type="button"
                        data-testid="speed-preview-acknowledge-btn"
                        onClick={() => onSpeedAcknowledgedChange?.(true)}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                          'border-accent/50 bg-accent/10 text-accent hover:bg-accent/20',
                        )}
                      >
                        <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        I understand — apply speed scoring
                      </button>
                    </div>
                  )}

                  {previewState.status === 'no_sample' && (
                    <div
                      className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-3 flex items-start gap-2"
                      data-testid="speed-preview-no-sample"
                    >
                      <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted" />
                      <p className="text-xs text-text-muted">
                        We'll show the score impact once this template has an athlete evaluation.
                      </p>
                    </div>
                  )}

                  {previewState.status === 'error' && (
                    <div
                      className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2"
                      data-testid="speed-preview-error"
                    >
                      <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
                      <p className="text-xs text-amber-200">
                        Could not load the score preview — you can still save, but review the impact
                        after saving by checking a recent athlete evaluation.
                      </p>
                    </div>
                  )}
                </>
              ) : null /* acknowledged — preview clears so the coach can focus on the form */}
            </div>
          )}

          {/* On-state: per-phase speed controls */}
          {speedOn && phases.length > 0 && onPhaseSpeedIntentChange && (
            <div className="space-y-3">
              {/* Glyph timeline — always-visible summary */}
              <div
                className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-3 space-y-2"
                data-testid="speed-glyph-timeline"
              >
                <p className="text-xs font-medium text-text-secondary">Speed summary</p>
                <div className="flex flex-wrap gap-2">
                  {phases.map((phase) => {
                    const intent = phaseSpeedIntents[phase.id] ?? 'match';
                    const opt = getSpeedOption(intent);
                    return (
                      <span
                        key={phase.id}
                        className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-dark-elevated-2 px-2 py-0.5 text-xs text-text-secondary"
                        title={opt.label}
                        data-testid={`speed-glyph-${phase.id}`}
                      >
                        <span aria-hidden="true">{opt.glyph}</span>
                        <span className="max-w-[100px] truncate">{phase.name || phase.id}</span>
                      </span>
                    );
                  })}
                </div>
                {/* Plain-language echo */}
                <ul className="space-y-0.5">
                  {phases.map((phase) => {
                    const intent = phaseSpeedIntents[phase.id] ?? 'match';
                    return (
                      <li key={phase.id} className="text-xs text-text-muted">
                        {getSpeedEcho(phase.name || phase.id, intent)}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Short-phase warnings */}
              {shortPhaseWarnings.length > 0 && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-1">
                  {shortPhaseWarnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-200 flex items-start gap-1.5">
                      <span aria-hidden="true" className="flex-shrink-0">⚠</span>
                      {w}
                    </p>
                  ))}
                </div>
              )}

              {/* "▸ Customize per phase" disclosure */}
              <button
                type="button"
                aria-expanded={phaseCustomizeExpanded}
                data-testid="speed-customize-toggle"
                onClick={() => setPhaseCustomizeExpanded((prev) => !prev)}
                className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                <ChevronDownIcon
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-150',
                    phaseCustomizeExpanded ? 'rotate-0' : '-rotate-90'
                  )}
                />
                {phaseCustomizeExpanded ? 'Collapse per-phase settings' : '▸ Customize per phase'}
              </button>

              {/* Per-phase dropdowns (only shown when disclosure is open) */}
              <div
                className={cn(
                  'grid transition-[grid-template-rows] duration-150 ease-out',
                  phaseCustomizeExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
                data-testid="speed-per-phase-panel"
              >
                <div className="overflow-hidden">
                  <div className="space-y-3 pt-1">
                    <p className="text-xs text-text-muted">
                      The default is "Same as reference" for all phases. Customize below to reward
                      faster or slower execution in specific segments.
                    </p>
                    {phases.map((phase) => {
                      const storedIntent: SpeedIntent = phaseSpeedIntents[phase.id] ?? 'match';
                      const selectedOpt = getSpeedOption(storedIntent);
                      return (
                        <div key={phase.id} className="space-y-1">
                          <div className="flex items-center justify-between gap-3">
                            <span className="min-w-0 truncate text-xs font-medium text-text-secondary">
                              {phase.name || `Phase ${phase.id}`}
                            </span>
                            {/* PRESERVE (A2): onChange writes storedValue (old vocab) — NOT the display label. */}
                            <select
                              value={storedIntent}
                              onChange={(e) =>
                                onPhaseSpeedIntentChange(phase.id, e.target.value as SpeedIntent)
                              }
                              className="shrink-0 rounded border border-border-subtle bg-dark-elevated-1 px-2 py-1 text-xs text-text-secondary focus:border-accent focus:outline-none"
                              aria-label={`Target speed for ${phase.name || phase.id}`}
                              data-testid={`speed-intent-select-${phase.id}`}
                            >
                              {SPEED_INTENT_OPTIONS.map((opt) => (
                                <option key={opt.storedValue} value={opt.storedValue}>
                                  {opt.glyph} {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {/* Per-option help text */}
                          <p className="text-xs text-text-muted pl-0">{selectedOpt.help}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* On-state: no phases defined */}
          {speedOn && phases.length === 0 && (
            <p className="text-xs text-text-muted">
              Define phases in Step 2 to set per-phase speed targets.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── Scoring Emphasis ───────────────────────────────────────────────── */}
      <Card variant="outlined" className="border-border-subtle">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent text-base font-bold">
              2
            </span>
            <div>
              <CardTitle className="text-base text-text-primary">Scoring Emphasis</CardTitle>
              <p className="text-sm text-text-muted mt-0.5">
                Choose what matters most when scoring this movement
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <ScoringEmphasisPanel
            bodyFormWeight={globalBodyMatchWeight}
            onBodyFormWeightChange={onGlobalBodyMatchWeightChange}
            bodyFormEnabled={globalBodyMatchEnabled}
            onBodyFormEnabledChange={onGlobalBodyMatchEnabledChange}
            eomWeight={economyOfMotionWeight}
            onEomWeightChange={onEconomyOfMotionWeightChange}
            /* D-A (b), 2026-10-09 — read-only, so the panel can tell the coach
               when their checkpoints currently carry no weight. */
            annotations={annotations}
          />
        </CardContent>
      </Card>

      {/* ── Focus Regions ──────────────────────────────────────────────────── */}
      <Card variant="outlined" className="border-border-subtle">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-dark-elevated-2 text-text-secondary text-base font-bold">
              3
            </span>
            <div>
              <CardTitle className="text-base text-text-primary">Focus Regions</CardTitle>
              <p className="text-sm text-text-muted mt-0.5">
                Mark specific body parts for detailed per-region tracking
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <RegionAnnotator
            ref={regionAnnotatorRef}
            videoId={videoId}
            videoUrl={videoUrl}
            fps={fps}
            totalFrames={totalFrames}
            videoWidth={videoWidth}
            videoHeight={videoHeight}
            annotations={annotations}
            onAnnotationsChange={onAnnotationsChange}
            templateHandedness={templateHandedness}
            detectedLeadSide={detectedLeadSide}
            phases={phases}
            cameraView={cameraView}
            templateId={templateId}
            declaredLeadSide={declaredLeadSide}
            openCheckpointId={openCheckpointId}
            onCheckpointOpened={onCheckpointOpened}
          />

          {/* Contextual help */}
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-border-subtle bg-dark-elevated-1 p-3">
            <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-text-muted" />
            <p className="text-sm text-text-secondary">
              These regions get individual Procrustes analysis. Marking an arm, leg, or torso means
              the AI will closely track that body part's alignment with the template across each
              evaluation phase.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── Goals (Goal Score PR-7b) ─────────────────────────────────────── */}
      <Card variant="outlined" className="border-border-subtle">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-dark-elevated-2 text-text-secondary text-base font-bold">
              4
            </span>
            <div>
              <CardTitle className="text-base text-text-primary">Goals</CardTitle>
              <p className="text-sm text-text-muted mt-0.5">
                Optional bonus targets athletes can beat for extra points
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <GoalAuthoringSection
            annotations={annotations}
            onAnnotationsChange={onAnnotationsChange}
            phases={phases}
            templateId={templateId}
            formEffectCurve={formEffectCurve}
            onFormEffectCurveChange={onFormEffectCurveChange}
            onRequestPickFromVideo={(onSelect) =>
              regionAnnotatorRef.current?.requestLandmarkSelection(onSelect)
            }
            onCancelPickFromVideo={() => regionAnnotatorRef.current?.cancelLandmarkSelection()}
          />
        </CardContent>
      </Card>

      {/* How scoring works - collapsible */}
      <div
        className={cn(
          'rounded-lg border overflow-hidden transition-shadow duration-200 hover:shadow-sm',
          'bg-info/10 border-info/30'
        )}
      >
        <button
          type="button"
          onClick={() => setHowItWorksExpanded((prev) => !prev)}
          aria-expanded={howItWorksExpanded}
          className={cn(
            'w-full flex items-center justify-between gap-3 p-4',
            'text-left font-semibold text-text-primary',
            'transition-colors duration-150 hover:bg-info/20',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-info'
          )}
        >
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-info" />
            <span>How scoring works</span>
          </div>
          <ChevronDownIcon
            className={cn(
              'h-5 w-5 flex-shrink-0 text-info',
              'transition-transform duration-200 ease-out',
              howItWorksExpanded && 'rotate-180'
            )}
          />
        </button>

        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            howItWorksExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="overflow-hidden">
            <div className="px-4 pb-4 pt-1">
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-info" />
                  <span>
                    <strong className="text-text-primary">My Goals</strong> are the specific checkpoints
                    you define — distance, axis, timing, joint angle. Higher goal weight means
                    hitting these checkpoints drives the score.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-info" />
                  <span>
                    <strong className="text-text-primary">Full-Body Form</strong> compares the athlete's
                    overall body shape to the template frame-by-frame. Higher weight catches
                    major posture or stance deviations not covered by goals.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-info" />
                  <span>
                    <strong className="text-text-primary">Economy of Motion</strong> penalizes unnecessary
                    movement — scooping, looping paths, or wasted motion. Higher weight rewards
                    precise, efficient execution.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-info" />
                  <span>
                    The <strong className="text-text-primary">Focus Regions</strong> you mark below
                    give specific body parts higher precision tracking within the Body Form score.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { ScoringStudio };
export default ScoringStudio;
